import React from "react";
import { useTranslation } from "react-i18next";
import {
    Card,
    SectionTitle,
    IconGrid,
    IconOption,
    IconUploadWrapper,
    IconUploadButton,
    IconDeleteBadge,
    IconDimensions,
} from "./sectionIconCardStyles";
import { CUSTOM_ICON_KEY } from "./constants";

export type SectionIconItem = {
    key: string;
    label: string;
    path: string;
};

type CommonProps = {
    mainColor: string;
    sectionIcons: SectionIconItem[];
    selectedIcon: string;
    onIconSelect: (key: string) => void;
    iconFileInputRef: React.RefObject<HTMLInputElement>;
    onIconUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    testIdPrefix: string;
};

type SingleProps = CommonProps & {
    mode: "single";
    customIconUrl: string;
    onRemoveCustomIcon: () => void;
};

type MultiProps = CommonProps & {
    mode: "multi";
    customIconUrls: string[];
    selectedCustomIdx: number;
    onSelectCustomIcon: (idx: number) => void;
    onRemoveCustomIcon: (idx: number) => void;
};

export type SectionIconCardProps = SingleProps | MultiProps;

export function SectionIconCard(props: SectionIconCardProps): JSX.Element {
    const { t } = useTranslation();
    const {
        mainColor,
        sectionIcons,
        selectedIcon,
        onIconSelect,
        iconFileInputRef,
        onIconUpload,
        testIdPrefix,
    } = props;

    return (
        <Card data-testid={`${testIdPrefix}-section-icon-card`}>
            <SectionTitle style={{ marginBottom: 15 }}>
                {t("Section icon")}
            </SectionTitle>
            <IconGrid data-testid={`${testIdPrefix}-icon-grid`}>
                {sectionIcons.map((icon) => (
                    <IconOption
                        key={icon.key}
                        data-testid={`${testIdPrefix}-icon-${icon.key}`}
                        isActive={selectedIcon === icon.key}
                        mainColor={mainColor}
                        onClick={() => onIconSelect(icon.key)}
                        title={icon.label}
                    >
                        <svg viewBox="0 0 24 24">
                            <path d={icon.path} />
                        </svg>
                    </IconOption>
                ))}

                {props.mode === "multi" &&
                    props.customIconUrls.map((url, idx) => (
                        <IconUploadWrapper key={`custom-${idx}`}>
                            <IconUploadButton
                                data-testid={`${testIdPrefix}-custom-icon-${idx}`}
                                mainColor={mainColor}
                                hasImage
                                isActive={
                                    selectedIcon === CUSTOM_ICON_KEY &&
                                    props.selectedCustomIdx === idx
                                }
                                onClick={() => props.onSelectCustomIcon(idx)}
                                title={t("Custom icon")}
                            >
                                <img src={url} alt="custom icon" />
                            </IconUploadButton>
                            <IconDeleteBadge
                                data-testid={`${testIdPrefix}-custom-icon-delete-${idx}`}
                                mainColor={mainColor}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onRemoveCustomIcon(idx);
                                }}
                                title={t("Delete")}
                            >
                                ×
                            </IconDeleteBadge>
                        </IconUploadWrapper>
                    ))}

                <IconUploadWrapper>
                    <IconUploadButton
                        data-testid={`${testIdPrefix}-icon-upload-btn`}
                        mainColor={mainColor}
                        hasImage={
                            props.mode === "single" && !!props.customIconUrl
                        }
                        onClick={() => iconFileInputRef.current?.click()}
                        title={t("Upload custom icon")}
                    >
                        {props.mode === "single" && props.customIconUrl ? (
                            <img src={props.customIconUrl} alt="custom icon" />
                        ) : (
                            <svg viewBox="0 0 24 24">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                            </svg>
                        )}
                    </IconUploadButton>
                    {props.mode === "single" && props.customIconUrl && (
                        <IconDeleteBadge
                            data-testid={`${testIdPrefix}-icon-delete-badge`}
                            mainColor={mainColor}
                            onClick={(e) => {
                                e.stopPropagation();
                                props.onRemoveCustomIcon();
                            }}
                            title={t("Delete")}
                        >
                            ×
                        </IconDeleteBadge>
                    )}
                </IconUploadWrapper>
            </IconGrid>
            <IconDimensions>
                PNG, SVG — 48x48px {t("recommended")} — max 500KB
            </IconDimensions>
            <input
                data-testid={`${testIdPrefix}-icon-file-input`}
                ref={iconFileInputRef}
                type="file"
                accept="image/png,image/svg+xml"
                style={{ display: "none" }}
                onChange={onIconUpload}
            />
        </Card>
    );
}
